import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { employeesApi } from "@/services/api";
import { useRoleGuard, ROLE_GROUPS } from "@/hooks/useRoleGuard";
import { usePagePermission } from "@/hooks/usePagePermission";

export interface Employee {
  id: string;                                          // UUID string now
  empId: string;
  emp_id?: string;
  name: string;
  role: string;
  department: string;
  email: string;
  phone: string;
  type: "Full Time" | "Freelancer" | "Contract";
  status: "active" | "on_leave" | "inactive";         // lowercase from DB
  utilization: number;
  activeJobs: number;
  active_jobs?: number;
  completedJobs: number;
  completed_jobs?: number;
  skills: string[];
  avatar: string;
  photo_url?: string;                                  // actual image URL
  joinedDate: string;
  joined_date?: string;
  user_id?: string;
}

const ROLE_OPTIONS = [
  "Survey Manager", "Field Surveyor", "Senior Surveyor", "CAD Technician",
  "GIS Analyst", "Drone Operator", "Lab Technician", "Project Coordinator",
  "Office Administrator", "Accounts Executive", "HR Manager", "Director", "Managing Director",
];

const DEPT_OPTIONS = ["Field Operations", "Office", "Management", "Lab"];
const TYPE_OPTIONS = ["Full Time", "Freelancer", "Contract"];
const STATUS_OPTIONS = ["active", "on_leave", "inactive"];

const deptColors: Record<string, { bg: string; text: string }> = {
  "Field Operations": { bg: "#fef3c7", text: "#92400e" },
  "Office":           { bg: "#fffbeb", text: "#b45309" },
  "Management":       { bg: "#fef9c3", text: "#854d0e" },
  "Lab":              { bg: "#fef3c7", text: "#78350f" },
};
const statusColors: Record<string, { bg: string; text: string }> = {
  "active":    { bg: "#ecfdf5", text: "#16a34a" },
  "on_leave":  { bg: "#fffbeb", text: "#d97706" },
  "inactive":  { bg: "#f3f4f6", text: "#6b7280" },
  // legacy display labels
  "Active":    { bg: "#ecfdf5", text: "#16a34a" },
  "On Leave":  { bg: "#fffbeb", text: "#d97706" },
  "Inactive":  { bg: "#f3f4f6", text: "#6b7280" },
};

function mapEmpType(raw: string): "Full Time" | "Freelancer" | "Contract" {
  if (raw === "freelancer") return "Freelancer";
  if (raw === "contract") return "Contract";
  return "Full Time";
}

function formatStatus(s: string): string {
  if (s === "on_leave") return "On Leave";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function UtilizationBar({ value }: { value: number }) {
  const color = value >= 80 ? "#ef4444" : value >= 60 ? "#f59e0b" : "#10b981";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="text-xs font-semibold w-8 text-right" style={{ color }}>{value}%</span>
    </div>
  );
}

function getPasswordStrength(pw: string): { label: string; color: string; width: string } {
  if (!pw) return { label: "", color: "", width: "0%" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: "Weak", color: "#ef4444", width: "25%" };
  if (score === 2) return { label: "Fair", color: "#f59e0b", width: "50%" };
  if (score === 3) return { label: "Good", color: "#10b981", width: "75%" };
  return { label: "Strong", color: "#059669", width: "100%" };
}

interface EmpForm {
  name: string; empId: string; role: string; department: string;
  email: string; phone: string; type: string; status: string; password: string;
}

const defaultForm: EmpForm = {
  name: "", empId: "", role: "", department: "Field Operations",
  email: "", phone: "", type: "Full Time", status: "active", password: "",
};

// ─── Reusable Form Fields ────────────────────────────────────────────────────
function EmpFormFields({
  form, onChange, showPassword, onTogglePassword, isEdit = false,
}: {
  form: EmpForm;
  onChange: (key: keyof EmpForm, val: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  isEdit?: boolean;
}) {
  const pwStrength = getPasswordStrength(form.password);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label>
          <input
            type="text" placeholder="e.g. Ravi Kumar"
            value={form.name} onChange={(e) => onChange("name", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Employee ID *</label>
          <input
            type="text" placeholder="e.g. EMP-011"
            value={form.empId} onChange={(e) => onChange("empId", e.target.value)}
            disabled={isEdit}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 disabled:bg-gray-50 disabled:text-gray-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input
            type="email" placeholder="email@company.com"
            value={form.email} onChange={(e) => onChange("email", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
          <input
            type="text" placeholder="+91 XXXXX XXXXX"
            value={form.phone} onChange={(e) => onChange("phone", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Role / Designation *</label>
          <select
            value={form.role} onChange={(e) => onChange("role", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
          >
            <option value="">Select role...</option>
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
          <select
            value={form.department} onChange={(e) => onChange("department", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
          >
            {DEPT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Employment Type</label>
        <div className="flex gap-2 flex-wrap">
          {TYPE_OPTIONS.map((t) => (
            <button key={t} type="button" onClick={() => onChange("type", t)}
              className={`px-3 py-1.5 border rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                form.type === t ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-600 hover:border-amber-300"
              }`}
            >{t}</button>
          ))}
        </div>
      </div>

      {isEdit && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTIONS.map((s) => (
              <button key={s} type="button" onClick={() => onChange("status", s)}
                className={`px-3 py-1.5 border rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                  form.status === s ? "border-amber-400 bg-amber-50 text-amber-700" : "border-gray-200 text-gray-600 hover:border-amber-300"
                }`}
              >{formatStatus(s)}</button>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs font-medium text-gray-600">
            {isEdit ? "Change Password" : "Default Login Password"}
          </label>
          <span className="text-xs text-gray-400">
            {isEdit ? "Leave blank to keep current" : "Optional — required for login access"}
          </span>
        </div>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder={isEdit ? "Enter new password..." : "Set a default password..."}
            value={form.password} onChange={(e) => onChange("password", e.target.value)}
            className="w-full pr-10 pl-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
          />
          <button type="button" onClick={onTogglePassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-sm`} />
          </button>
        </div>
        {form.password && (
          <div className="mt-2">
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ width: pwStrength.width, background: pwStrength.color }} />
            </div>
            <p className="text-xs mt-1 font-medium" style={{ color: pwStrength.color }}>{pwStrength.label}</p>
          </div>
        )}
        {!isEdit && (
          <p className="text-xs text-gray-400 mt-1.5">
            <i className="ri-information-line mr-1" />
            If left blank, the employee won&apos;t have login access until a password is set.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function EmployeesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { hasPermission: canViewEmployees, user, loading: permLoading } = usePagePermission("Employees", "view", true);
  const canManage = user ? ROLE_GROUPS.managers.includes(user.role) : false;
  const canDelete = user ? ["admin", "superadmin", "super_admin"].includes(user.role) : false;

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [deptFilter, setDeptFilter] = useState(searchParams.get("dept") || "All");

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState<EmpForm>(defaultForm);
  const [addShowPw, setAddShowPw] = useState(false);
  const [addSaving, setAddSaving] = useState(false);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Employee | null>(null);
  const [editForm, setEditForm] = useState<EmpForm>(defaultForm);
  const [editShowPw, setEditShowPw] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const departments = ["All", ...DEPT_OPTIONS];

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const result = await employeesApi.getAll();
      const list = result?.data || result || [];
      // Normalize API response to component shape
      const normalized = (Array.isArray(list) ? list : []).map((u: Record<string, unknown>) => ({
        ...u,
        empId: (u.emp_id as string) || "",
        activeJobs: (u.active_jobs as number) || 0,
        completedJobs: (u.completed_jobs as number) || 0,
        joinedDate: (u.joined_date as string) || "",
        type: mapEmpType(u.emp_type as string || u.type as string || "full_time"),
      }));
      setEmployees(normalized as Employee[]);
    } catch {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  // Sync URL params with filter state
  useEffect(() => {
    const params: Record<string, string> = {};
    if (deptFilter !== "All") params.dept = deptFilter;
    if (search.trim()) params.search = search.trim();
    setSearchParams(params, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptFilter, search]);

  // ── Add ──────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!addForm.name || !addForm.empId || !addForm.role) return;
    setAddSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: addForm.name, emp_id: addForm.empId, role: addForm.role,
        department: addForm.department, email: addForm.email || null,
        phone: addForm.phone || null, type: addForm.type,
        status: "Active", utilization: 0, active_jobs: 0, completed_jobs: 0,
        skills: [], joined_date: new Date().toISOString().split("T")[0],
      };
      if (addForm.password.trim()) payload.password = addForm.password.trim();
      const created = await employeesApi.create(payload);
      const newEmp = created?.data || created;
      if (newEmp) setEmployees((prev) => [newEmp, ...prev]);
      setShowAddModal(false);
      setAddForm(defaultForm);
      setAddShowPw(false);
      showToast("success", `${addForm.name} added successfully`);
    } catch (err: unknown) {
      showToast("error", err instanceof Error ? err.message : "Failed to add employee");
    } finally {
      setAddSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const openEdit = (emp: Employee, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTarget(emp);
    setEditForm({
      name: emp.name, empId: emp.empId || emp.emp_id || "",
      role: emp.role, department: emp.department,
      email: emp.email || "", phone: emp.phone || "",
      type: emp.type, status: emp.status || "active", password: "",
    });
    setEditShowPw(false);
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!editTarget || !editForm.name || !editForm.role) return;
    setEditSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: editForm.name, role: editForm.role, department: editForm.department,
        email: editForm.email || null, phone: editForm.phone || null,
        type: editForm.type, status: editForm.status, skills: editTarget.skills || [],
      };
      if (editForm.password.trim()) payload.password = editForm.password.trim();
      const updated = await employeesApi.update(editTarget.id, payload);
      const updatedEmp = updated?.data || updated;
      setEmployees((prev) => prev.map((e) => e.id === editTarget.id ? { ...e, ...(updatedEmp || {}), ...editForm } : e));
      setShowEditModal(false);
      setEditTarget(null);
      showToast("success", `${editForm.name} updated successfully`);
    } catch (err: unknown) {
      showToast("error", err instanceof Error ? err.message : "Failed to update employee");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const openDelete = (emp: Employee, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(emp);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await employeesApi.delete(deleteTarget.id);
      setEmployees((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      showToast("success", `${deleteTarget.name} removed successfully`);
    } catch (err: unknown) {
      showToast("error", err instanceof Error ? err.message : "Failed to delete employee");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = employees.filter((e) => {
    const matchSearch =
      e.name?.toLowerCase().includes(search.toLowerCase()) ||
      e.role?.toLowerCase().includes(search.toLowerCase()) ||
      (e.empId || e.emp_id || "").toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "All" || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  const totalActive = employees.filter((e) => e.status === "active" || e.status === "Active").length;
  const avgUtilization = employees.length
    ? Math.round(employees.reduce((a, b) => a + (b.utilization || 0), 0) / employees.length) : 0;
  const overloaded = employees.filter((e) => (e.utilization || 0) >= 80).length;
  const totalActiveJobs = employees.reduce((a, b) => a + (b.activeJobs ?? b.active_jobs ?? 0), 0);

  if (!permLoading && user && !canViewEmployees) {
    return (
      <ERPLayout title="Employee Management">
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-2-line text-3xl text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-sm text-gray-500 max-w-sm mb-6">
            Your role <strong>({user.role})</strong> does not have permission to view Employees.
          </p>
        </div>
      </ERPLayout>
    );
  }

  return (
    <ERPLayout title="Employee Management">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium shadow-lg transition-all ${
          toast.type === "success" ? "bg-amber-600 text-white" : "bg-red-500 text-white"
        }`}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
          {toast.msg}
        </div>
      )}

      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Employee &amp; Team Management</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {loading ? "Loading..." : `${employees.length} total employees`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchEmployees}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
              </span>
              Refresh
            </button>
            {canManage && (
              <button onClick={() => { setAddForm(defaultForm); setAddShowPw(false); setShowAddModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>
                Add Employee
              </button>
            )}
          </div>
        </div>

        {!canManage && user && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50">
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-eye-line text-amber-500" />
            </span>
            <p className="text-sm text-amber-700">You have <strong>view-only</strong> access. Contact an admin to make changes.</p>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
                <div className="w-9 h-9 rounded-xl bg-gray-100 mb-2" />
                <div className="h-7 bg-gray-100 rounded w-12 mb-1" />
                <div className="h-3 bg-gray-100 rounded w-24" />
              </div>
            ))
          ) : (
            [
              { label: "Active Employees", value: totalActive, icon: "ri-user-line", color: "#10b981", bg: "#ecfdf5", path: "/employees", query: "status=active" },
              { label: "Avg. Utilization", value: `${avgUtilization}%`, icon: "ri-pie-chart-line", color: "#3b82f6", bg: "#eff6ff", path: "/employees" },
              { label: "Overloaded (≥80%)", value: overloaded, icon: "ri-alarm-warning-line", color: "#ef4444", bg: "#fef2f2", path: "/employees" },
              { label: "Active Job Assignments", value: totalActiveJobs, icon: "ri-file-list-3-line", color: "#f59e0b", bg: "#fffbeb", path: "/job-cards" },
            ].map((k) => (
              <div
                key={k.label}
                onClick={() => navigate(k.query ? `${k.path}?${k.query}` : k.path)}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:border-amber-200 hover:shadow-sm transition-all cursor-pointer group"
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: k.bg }}>
                  <i className={`${k.icon} text-base`} style={{ color: k.color }} />
                </div>
                <p className="text-2xl font-bold text-gray-900 group-hover:text-amber-700 transition-colors">{k.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
              </div>
            ))
          )}
        </div>

        {/* Workload Overview */}
        {!loading && employees.filter((e) => e.status === "Active").length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Team Workload Overview</h3>
            <div className="space-y-3">
              {employees
                .filter((e) => e.status === "active" || e.status === "Active")
                .sort((a, b) => (b.utilization || 0) - (a.utilization || 0))
                .map((emp) => {
                  const dc = deptColors[emp.department] || { bg: "#f3f4f6", text: "#374151" };
                  const initials = emp.avatar || emp.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "??";
                  return (
                    <div key={emp.id} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {initials}
                      </div>
                      <div className="w-28 flex-shrink-0">
                        <p className="text-xs font-semibold text-gray-800 truncate">{emp.name}</p>
                        <p className="text-xs text-gray-400 truncate">{emp.role}</p>
                      </div>
                      <span className="text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 hidden sm:block" style={{ background: dc.bg, color: dc.text }}>
                        {emp.department.split(" ")[0]}
                      </span>
                      <div className="flex-1">
                        <UtilizationBar value={emp.utilization || 0} />
                      </div>
                      <span className="text-xs text-gray-500 flex-shrink-0 hidden md:block">{emp.activeJobs ?? emp.active_jobs ?? 0} active</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-sm" />
            </span>
            <input
              type="text" placeholder="Search name, role, employee ID..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
            />
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
            {departments.map((d) => (
              <button key={d} onClick={() => setDeptFilter(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${deptFilter === d ? "bg-white text-amber-700 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
              >{d}</button>
            ))}
          </div>
        </div>

        {/* Employee Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gray-100 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-32" />
                    <div className="h-3 bg-gray-100 rounded w-24" />
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full mb-3" />
                <div className="h-3 bg-gray-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((emp) => {
              const dc = deptColors[emp.department] || { bg: "#f3f4f6", text: "#374151" };
              const sc = statusColors[emp.status] || { bg: "#f3f4f6", text: "#374151" };
              const initials = emp.avatar || emp.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "??";
              const empDisplayId = emp.empId || emp.emp_id || "";
              const activeJobCount = emp.activeJobs ?? emp.active_jobs ?? 0;
              const completedJobCount = emp.completedJobs ?? emp.completed_jobs ?? 0;
              return (
                <div key={emp.id}
                  className="bg-white rounded-xl border border-gray-100 hover:border-amber-200 transition-all cursor-pointer group overflow-hidden"
                  onClick={() => navigate(`/employees/${emp.id}`)}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-11 h-11 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900">{emp.name}</p>
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: sc.bg, color: sc.text }}>{formatStatus(emp.status)}</span>
                        </div>
                        <p className="text-xs text-gray-500">{emp.role}</p>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block" style={{ background: dc.bg, color: dc.text }}>{emp.department}</span>
                      </div>
                      <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{empDisplayId}</span>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {(emp.skills || []).slice(0, 3).map((s) => (
                        <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                      {(emp.skills || []).length > 3 && (
                        <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">+{emp.skills.length - 3}</span>
                      )}
                    </div>

                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Workload</span>
                        <span className="text-xs font-semibold" style={{ color: (emp.utilization || 0) >= 80 ? "#ef4444" : (emp.utilization || 0) >= 60 ? "#f59e0b" : "#10b981" }}>
                          {emp.utilization || 0}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{
                          width: `${emp.utilization || 0}%`,
                          background: (emp.utilization || 0) >= 80 ? "#ef4444" : (emp.utilization || 0) >= 60 ? "#f59e0b" : "#10b981",
                        }} />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-loader-4-line text-amber-500 text-xs" /></span>
                        {activeJobCount} active
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-checkbox-circle-line text-amber-500 text-xs" /></span>
                        {completedJobCount} done
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-briefcase-line text-gray-400 text-xs" /></span>
                        {emp.type}
                      </span>
                    </div>
                  </div>

                  <div className="px-4 py-2.5 border-t border-gray-50 bg-gray-50/50 flex items-center justify-between">
                    <span className="text-xs text-gray-400">Joined {emp.joinedDate}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.id}`); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                        title="View details"
                      >
                        <i className="ri-eye-line text-sm" />
                      </button>
                      {canManage && (
                        <button
                          onClick={(e) => openEdit(emp, e)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 cursor-pointer transition-all"
                          title="Edit employee"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => openDelete(emp, e)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer transition-all"
                          title="Delete employee"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-team-line text-4xl text-gray-200" />
            </span>
            <p className="text-gray-400 text-sm">No employees found</p>
            {canManage && (
              <button onClick={() => setShowAddModal(true)}
                className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
              >
                Add First Employee
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Add Employee Modal ─────────────────────────────────────────────── */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-gray-900">Add New Employee</h3>
                <p className="text-xs text-gray-400 mt-0.5">Creates employee record + user login if password is set</p>
              </div>
              <button onClick={() => { setShowAddModal(false); setAddForm(defaultForm); setAddShowPw(false); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <EmpFormFields
                form={addForm}
                onChange={(k, v) => setAddForm((f) => ({ ...f, [k]: v }))}
                showPassword={addShowPw}
                onTogglePassword={() => setAddShowPw((v) => !v)}
              />
            </div>
            <div className="px-6 pb-6 flex items-center gap-3 flex-shrink-0">
              <button onClick={() => { setShowAddModal(false); setAddForm(defaultForm); setAddShowPw(false); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >Cancel</button>
              <button onClick={handleAdd}
                disabled={addSaving || !addForm.name || !addForm.empId || !addForm.role}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addSaving ? "Adding..." : "Add Employee"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Employee Modal ────────────────────────────────────────────── */}
      {showEditModal && editTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-gray-900">Edit Employee</h3>
                <p className="text-xs text-gray-400 mt-0.5">Changes sync to linked user account automatically</p>
              </div>
              <button onClick={() => { setShowEditModal(false); setEditTarget(null); setEditShowPw(false); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {/* Linked user badge */}
              {editTarget.user_id ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg mb-4">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-amber-600 text-sm" />
                  </span>
                  <p className="text-xs text-amber-700">Has a linked login account — changes will sync automatically</p>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg mb-4">
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-unfollow-line text-amber-600 text-sm" />
                  </span>
                  <p className="text-xs text-amber-700">No login account yet — set a password below to create one</p>
                </div>
              )}
              <EmpFormFields
                form={editForm}
                onChange={(k, v) => setEditForm((f) => ({ ...f, [k]: v }))}
                showPassword={editShowPw}
                onTogglePassword={() => setEditShowPw((v) => !v)}
                isEdit
              />
            </div>
            <div className="px-6 pb-6 flex items-center gap-3 flex-shrink-0">
              <button onClick={() => { setShowEditModal(false); setEditTarget(null); setEditShowPw(false); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >Cancel</button>
              <button onClick={handleEdit}
                disabled={editSaving || !editForm.name || !editForm.role}
                className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ───────────────────────────────────────────── */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-2-line text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-bold text-gray-900 text-center mb-1">Delete Employee?</h3>
            <p className="text-sm text-gray-500 text-center mb-1">
              You&apos;re about to remove <strong className="text-gray-800">{deleteTarget.name}</strong>
            </p>
            <p className="text-xs text-gray-400 text-center mb-5">
              This will remove the employee record and all job team assignments. Their user login account will be deactivated but not deleted.
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >Cancel</button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
